/* =============================================================================
   VIEWS / ADMIN / SETTINGS-COMPLIANCE-TSE — TSE/KassenSichV-Settings + Orphan-Card
   =============================================================================
   S66 Refactor: aus settings.js extrahiert (Zeilen 1583-1991, ~408 LOC).

   Inhalt:
     - tseSettingsSkeleton/Html        Card-Renderer mit Mock/TEST/LIVE-Modi
     - refreshTseSettings              Async-Loader für TSE-Status aus DB
     - tseOrphanSkeleton + 4 _format/_build-Helper (vitest-exportiert)
     - refreshTseOrphanCard            Async-Loader für Orphan-Tx-Liste
     - _wire/_unwireTseOrphanSubscription   Realtime-Subscription
     - tseOrphanRetry                  Re-Submit-Action für stehengebliebene Tx
     - buildTseModeSwitchConfirmMessage  Mode-Switch-Confirm-Modal-Text
     - tseSetMode + tseProvision       Action-Handler (Mode-Switch + Re-Provisioning)

   Memory `project_gg_fiskaly_setup`: V2 State-Flow, 5er-Slot-Limit, PUK nur
   einmal sichtbar. tseSetMode lädt nach Mode-Switch die TSE neu via Provisioning.

   Dependencies aus settings.js werden via window-Wrapper aufgelöst.
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { safeInvoke } from '../../core/edge-functions.js';

// -----------------------------------------------------------------------------
// Cross-Module Helpers (Anti-Circular)
// -----------------------------------------------------------------------------
function _renderSettings() {
  if (typeof window !== 'undefined' && typeof window.renderSettings === 'function') {
    window.renderSettings();
  }
}
// -----------------------------------------------------------------------------
// TSE & Compliance — Card-Renderer + Action-Handler
// -----------------------------------------------------------------------------

export function tseSettingsSkeleton() {
  return `
    <h2>TSE &amp; Compliance</h2>
    <p class="hint">Lade TSE-Status…</p>
  `;
}

export function tseSettingsHtml(config) {
  const mode      = String((config && config.tse_mode) || 'mock');
  const tssId     = String((config && config.tse_fiskaly_tss_id) || '');
  const clientId  = String((config && config.tse_fiskaly_client_id) || '');
  const env       = String((config && config.tse_fiskaly_environment) || 'TEST');
  const lastAudit = config && config.last_audit_at;
  const auditCnt  = (config && config.audit_count) || 0;
  const provisioned = !!(tssId && clientId);

  const modeBtn = (m, label, desc) => `
    <label class="tse-mode-option ${mode === m ? 'is-active' : ''}">
      <input type="radio" name="tse-mode" value="${m}" ${mode === m ? 'checked' : ''}
             onchange="tseSetMode('${m}')">
      <span class="tse-mode-label"><strong>${label}</strong><br><span class="hint">${desc}</span></span>
    </label>
  `;

  const provisionBlock = provisioned ? `
    <div class="tse-provision-status">
      <div class="tse-provision-line"><strong>TSS-ID:</strong> <code>${htmlEscape(tssId)}</code></div>
      <div class="tse-provision-line"><strong>Client-ID:</strong> <code>${htmlEscape(clientId)}</code></div>
      <div class="tse-provision-line"><strong>Umgebung:</strong> ${htmlEscape(env)}</div>
      <p class="hint" style="margin-top:6px;">
        Provisioning abgeschlossen. Modus auf <strong>fiskaly</strong> umschalten,
        damit jede Bezahlung live signiert wird.
      </p>
    </div>
  ` : `
    <div class="tse-provision-pending">
      <p class="hint">
        Noch nicht provisioniert. Voraussetzung: <code>FISKALY_API_KEY</code> +
        <code>FISKALY_API_SECRET</code> sind in Supabase Edge-Function-Secrets gesetzt.
        Der Klick legt einmalig eine TSS + einen Client bei fiskaly an.
      </p>
      <button class="btn btn-primary btn-sm" onclick="tseProvision()">
        🔐 TSS + Client einmalig anlegen
      </button>
    </div>
  `;

  return `
    <h2>TSE &amp; Compliance</h2>
    <p class="hint" style="margin-bottom: 14px;">
      Technische Sicherheits&shy;einrichtung gemäß KassenSichV (Pflicht für
      elektronische Aufzeichnungssysteme im Gastronomie-Bereich).
    </p>

    <div class="field">
      <label>Modus</label>
      <div class="tse-mode-group">
        ${modeBtn('disabled', 'Deaktiviert', 'Keine Signatur. Nur für Dev-Sessions ohne TSE.')}
        ${modeBtn('mock',     'Mock (Test)', 'Pseudo-Signaturen. Vorschau des kompletten Bon-Flows ohne fiskaly-Credentials.')}
        ${modeBtn('fiskaly',  'fiskaly (Live)', 'Echte KassenSichV-Signatur über fiskaly Cloud-TSE.')}
      </div>
    </div>

    <div class="field">
      <label>fiskaly Provisioning</label>
      ${provisionBlock}
    </div>

    <div class="field">
      <label>Statistik</label>
      <div class="hint">
        Letzte Signatur:
        <strong>${lastAudit ? htmlEscape(new Date(lastAudit).toLocaleString('de-DE')) : '—'}</strong>
        · Audit-Einträge gesamt: <strong>${auditCnt}</strong>
      </div>
    </div>
  `;
}

/** Async-Refresh nach renderSettings. */
export async function refreshTseSettings() {
  const root = document.getElementById('tse-settings-card');
  if (!root) return;
  if (typeof window.getTseConfig !== 'function') {
    root.innerHTML = '<h2>TSE &amp; Compliance</h2><p class="hint">TSE-Adapter nicht geladen.</p>';
    return;
  }
  try {
    const cfg = await window.getTseConfig({ force: true });
    root.innerHTML = tseSettingsHtml(cfg);
  } catch (err) {
    console.error('[settings] TSE-Config laden:', err);
    root.innerHTML = '<h2>TSE &amp; Compliance</h2><p class="hint">TSE-Config konnte nicht geladen werden: '
                   + htmlEscape(err.message || String(err)) + '</p>';
  }
}

/* ============================================================================
 * S55 — TSE-Orphan-View (Distributed-TX-Pattern Block N)
 * ============================================================================
 * Lese-/Action-Schale über die RPC `tse_pending_list_orphans` aus
 * Migration phase_43 (S54). Zeigt:
 *   - pending-Rows älter als 5 Min (hängen in fiskaly oder zwischen unserer
 *     DB und fiskaly stuck) — der pg_cron-Job `tse-reconcile-every-2min`
 *     räumt diese normalerweise selbst auf, aber bei Edge-Function-Ausfall
 *     bleiben sie hier sichtbar als Frühwarn-Indikator.
 *   - orphan_failed-Rows — endgültig hängen, brauchen manuelle Reconciliation
 *     oder fiskaly-Support-Eingriff.
 *
 * UI-Visibility: nur owner (Compliance-Tab ist owner-only via Phase 41 in
 * public/adapters/auth.js). RPC selbst checkt nochmal auf owner/admin und
 * wirft NICHT_BERECHTIGT bei anderen Rollen → Defense-in-Depth.
 *
 * Memory `feedback_gg_db_for_critical_data`, `feedback_gg_no_view_hijack`,
 * `feedback_gg_non_destructive_render`.
 * ============================================================================ */

/** Pure: HTML-Skeleton vor dem ersten Lade-Zyklus. */
export function tseOrphanSkeleton() {
  return `
    <h2>🚨 TSE-Pending, Orphans &amp; unsignierte Belege</h2>
    <p class="hint" style="margin-bottom: 12px;">
      Live-Sicht auf <code>tse_pending_tx</code> — Signaturen, die zwischen
      unserer DB und fiskaly hängen geblieben sind. Der Reconciliation-Job
      <code>tse-reconcile</code> probt alle 2 Minuten; was hier auftaucht,
      hat den automatischen Versuch entweder noch nicht durchlaufen
      (Pending > 5&nbsp;Min) oder endgültig verfehlt (Orphan).
      <br>
      Seit S258 steht darüber, was <em>gar nicht erst</em> bis dorthin kam:
      bezahlte Belege ohne Signatur (<code>tse_unsigned_receipts</code>). Die
      sieht sonst niemand — weder der Reconcile-Job noch diese Karte.
    </p>
    <div id="tse-orphan-body"><p class="hint">Lade…</p></div>
  `;
}

/** Pure: Label für den state-Wert (UI-freundlich). */
export function _formatTseOrphanState(state) {
  if (state === 'orphan_failed') return 'Orphan';
  if (state === 'reconciling')   return 'Reconciling';
  return 'Pending';
}

/** Pure: Badge-Inline-Style je nach state. */
export function _tseOrphanStateBadgeStyle(state) {
  if (state === 'orphan_failed') return 'background: rgba(193,75,75,.14); color: #8a1f1f;';
  if (state === 'reconciling')   return 'background: rgba(231,168,45,.14); color: #8a6614;';
  return 'background: rgba(45,124,231,.14); color: #1f4f8a;';
}

/** Pure: minutenweise Alterung des Zeitstempels gegenüber `nowMs`. */
export function _formatTseOrphanAge(createdAtIso, nowMs) {
  if (!createdAtIso) return '—';
  const t = Date.parse(createdAtIso);
  if (!isFinite(t)) return '—';
  const ageMin = Math.max(0, Math.floor((nowMs - t) / 60000));
  if (ageMin < 60) return ageMin + ' Min';
  const ageH = Math.floor(ageMin / 60);
  const rest = ageMin % 60;
  if (ageH < 24) return ageH + 'h ' + rest + 'm';
  const ageD = Math.floor(ageH / 24);
  return ageD + 'd ' + (ageH % 24) + 'h';
}

/** Pure: Betrag in Euro, deutsch. */
export function _formatCents(cents) {
  const n = Number(cents || 0) / 100;
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/**
 * S258 — Pure: die roten Zeilen des Wächters (`tse_unsigned_receipts`).
 *
 * Was hier steht, ist schwerer als ein Orphan: der Orphan hat es bis
 * `tse_pending_open` geschafft und der Reconcile-Job probt ihn weiter. Diese
 * Belege sind bezahlt, dem Gast angezeigt, archiviert — und es gibt nichts, das
 * sich selbst darum kümmert. Deshalb eine eigene Tabelle und nicht eine weitere
 * Spalte in der Orphan-Liste.
 */
export function _buildTseUnsignedTableHtml(rows, nowMs) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return '';
  const head = `
    <thead>
      <tr>
        <th style="text-align:left;">Art</th>
        <th style="text-align:left;">Beleg</th>
        <th style="text-align:left;">Betrag</th>
        <th style="text-align:left;">Bezahlt</th>
        <th style="text-align:left;">Alter</th>
        <th></th>
      </tr>
    </thead>`;
  const body = list.map(row => {
    const kind    = row.kind === 'order' ? 'order' : 'receipt';
    const kindLbl = kind === 'order' ? 'Bestellung' : 'Teilbeleg';
    const id      = htmlEscape(String(row.id || ''));
    const nr      = htmlEscape(String(row.invoiceNumber || '—'));
    const betrag  = htmlEscape(_formatCents(row.totalGrossCents));
    const paid    = row.paidAt
      ? htmlEscape(new Date(row.paidAt).toLocaleString('de-DE',
          { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }))
      : '—';
    const age     = htmlEscape(_formatTseOrphanAge(row.paidAt, nowMs));
    return `
      <tr data-tse-unsigned="${id}" data-tse-order="${htmlEscape(String(row.orderId || ''))}">
        <td><span class="badge" style="display:inline-block; padding:2px 8px; border-radius:6px; font-size:.78rem; background: rgba(193,75,75,.14); color: #8a1f1f;">${htmlEscape(kindLbl)}</span></td>
        <td><code style="font-size:.85rem;">${nr}</code></td>
        <td><strong>${betrag}</strong></td>
        <td>${paid}</td>
        <td>${age}</td>
        <td>
          <button type="button" class="btn btn-primary"
                  onclick="tseSignMissing('${htmlEscape(kind)}', '${id}')">
            🔏 Signatur nachholen
          </button>
        </td>
      </tr>
    `;
  }).join('');
  return `
    <div class="tse-unsigned-block" style="margin-bottom:18px;">
      <p class="hint" style="margin:0 0 8px; padding:12px; background: rgba(193,75,75,.10); border-radius:8px; color:#8a1f1f;">
        <strong>⚠️ ${list.length} bezahlte${list.length === 1 ? 'r' : ''} Beleg${list.length === 1 ? '' : 'e'} ohne TSE-Signatur.</strong>
        Diese Belege haben es nie bis <code>tse_pending_tx</code> geschafft — der
        Reconciliation-Job sieht sie nicht, und ohne diese Liste sähe sie niemand.
        § 146a AO verlangt die Signatur je Beleg; fehlt sie, muss der Ausfall
        nachweisbar sein. Grund und Zeitpunkt stehen im Prüfpfad
        (<code>order.tse_missing</code> / <code>receipt.tse_missing</code>).
      </p>
      <div class="table-wrap" style="overflow-x:auto;">
        <table class="data-table" style="width:100%;">
          ${head}
          <tbody>${body}</tbody>
        </table>
      </div>
    </div>
  `;
}

/**
 * S258 — Pure: der Karten-Rumpf aus beiden Listen.
 *
 * Das grüne „alles sauber" darf NUR stehen, wenn beide leer sind. Vorher hing
 * es allein an `tse_pending_tx` und meldete Ruhe, während ein bezahlter,
 * unsignierter Beleg danebenlag. Ein Panel, das in genau der Lage beruhigt,
 * für die es gebaut wurde, ist schlimmer als keines.
 */
export function _buildComplianceTseBodyHtml(orphans, unsigned, nowMs) {
  const o = Array.isArray(orphans)  ? orphans  : [];
  const u = Array.isArray(unsigned) ? unsigned : [];
  if (!o.length && !u.length) {
    return `
      <p class="hint" style="margin: 0; padding: 14px; background: rgba(45,124,231,.07); border-radius: 8px;">
        ✅ Keine pending- oder orphan-Rows älter als 5&nbsp;Minuten, und kein
        bezahlter Beleg ohne Signatur. Distributed-TX-Pattern läuft sauber.
      </p>
    `;
  }
  return _buildTseUnsignedTableHtml(u, nowMs) + _buildTseOrphanTableHtml(o, nowMs);
}

/** Pure: HTML-Render der Orphan-Tabelle. Leere Liste → leerer String
 *  (den „alles sauber"-Satz entscheidet seit S258 `_buildComplianceTseBodyHtml`,
 *  weil er auch den Wächter kennen muss). Exportiert für Vitest-Coverage. */
export function _buildTseOrphanTableHtml(rows, nowMs) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return '';
  const head = `
    <thead>
      <tr>
        <th style="text-align:left;">Status</th>
        <th style="text-align:left;">Order</th>
        <th style="text-align:left;">Alter</th>
        <th style="text-align:left;">Versuche</th>
        <th style="text-align:left;">Letzter Fehler</th>
        <th style="text-align:left;">Aktor</th>
        <th></th>
      </tr>
    </thead>`;
  const body = list.map(row => {
    const state = String(row.state || 'pending');
    const stateLabel = _formatTseOrphanState(state);
    const stateStyle = _tseOrphanStateBadgeStyle(state);
    const age = _formatTseOrphanAge(row.createdAt, nowMs);
    const order = htmlEscape(row.orderId || '—');
    const attempts = Number.isFinite(row.attemptCount) ? row.attemptCount : 0;
    const lastError = row.lastError
      ? `<code style="font-size:.78rem; color:var(--danger);">${htmlEscape(String(row.lastError).slice(0, 120))}</code>`
      : '<span class="hint">—</span>';
    const actor = htmlEscape(row.createdBy || '');
    const txEsc = htmlEscape(row.txId || '');
    return `
      <tr data-tse-tx="${txEsc}">
        <td><span class="badge" style="display:inline-block; padding:2px 8px; border-radius:6px; font-size:.78rem; ${stateStyle}">${htmlEscape(stateLabel)}</span></td>
        <td><code style="font-size:.85rem;">${order}</code></td>
        <td>${htmlEscape(age)}</td>
        <td>${attempts}</td>
        <td>${lastError}</td>
        <td><code style="font-size:.78rem; color: var(--ink-soft);">${actor}</code></td>
        <td>
          <button type="button" class="btn btn-secondary"
                  onclick="tseOrphanRetry('${txEsc}')">
            ${state === 'orphan_failed' ? '🔏 Neu signieren' : '🔁 Erneut probieren'}
          </button>
        </td>
      </tr>
    `;
  }).join('');
  return `
    <div class="table-wrap" style="overflow-x:auto;">
      <table class="data-table" style="width:100%;">
        ${head}
        <tbody>${body}</tbody>
      </table>
    </div>
    <p class="hint" style="margin-top:10px;">
      „Erneut probieren" ruft die Edge-Function <code>tse-reconcile</code> für
      diese eine TX. Ergebnis: <em>closed</em> (fiskaly bestätigt FINISHED) ·
      <em>orphan</em> (fiskaly 404 oder hängt > 15&nbsp;Min) · <em>still_pending</em>
      (fiskaly probt noch).
    </p>
  `;
}

/** Modul-State für Subscription-Cleanup. */
let _tseOrphanUnsub = null;
let _tseOrphanLoading = false;

/** Async-Refresh: liest aus RPC + zeichnet die Tabelle ins Skeleton-Body. */
export async function refreshTseOrphanCard() {
  const card = document.getElementById('tse-orphan-card');
  if (!card) return;
  // Skeleton ist evtl. weg, wenn ein anderer Sub-Tab gerade aktiv ist —
  // dann nichts tun. (renderSettings ruft uns nur wenn section=compliance.)
  let body = card.querySelector('#tse-orphan-body');
  if (!body) {
    card.innerHTML = tseOrphanSkeleton();
    body = card.querySelector('#tse-orphan-body');
  }
  if (_tseOrphanLoading) return;
  _tseOrphanLoading = true;

  const adapter = window.App && window.App.storage && window.App.storage.tsePendingTx;
  if (!adapter) {
    body.innerHTML = '<p class="hint">tsePendingTx-Adapter nicht geladen.</p>';
    _tseOrphanLoading = false;
    return;
  }
  try {
    // S258 — beide Listen, und zwar beide BEVOR gezeichnet wird: das grüne
    // „alles sauber" hängt an beiden. Der Wächter-Adapter kann auf einer alten
    // DB fehlen (phase_188 noch nicht appliziert) — dann bleibt es beim alten
    // Verhalten statt einer Fehlermeldung.
    const unsignedAdapter = window.App.storage.tseUnsigned;
    const [rows, unsigned] = await Promise.all([
      adapter.listOrphans({ limit: 50 }),
      unsignedAdapter ? unsignedAdapter.list({ limit: 100 }) : Promise.resolve([])
    ]);
    body.innerHTML = _buildComplianceTseBodyHtml(rows, unsigned, Date.now());
  } catch (err) {
    console.error('[settings] tse-orphan refresh:', err);
    body.innerHTML = '<p class="hint">Konnte Orphan-Liste nicht laden: '
                   + htmlEscape(err.message || String(err)) + '</p>';
  } finally {
    _tseOrphanLoading = false;
  }
}

/** Realtime-Subscribe einmalig beim Eintritt in den Compliance-Sub-Tab. */
export function _wireTseOrphanSubscription() {
  if (_tseOrphanUnsub) return; // idempotent
  const adapter = window.App && window.App.storage && window.App.storage.tsePendingTx;
  if (!adapter || typeof adapter.subscribe !== 'function') return;
  _tseOrphanUnsub = adapter.subscribe(() => {
    // Bei jedem tse_pending_tx-Event neu laden — niedrige Frequenz, kein Debounce nötig.
    refreshTseOrphanCard();
  });
}

/** Realtime-Subscribe beim Verlassen des Compliance-Tabs aufräumen. */
export function _unwireTseOrphanSubscription() {
  if (typeof _tseOrphanUnsub === 'function') {
    try { _tseOrphanUnsub(); }
    catch (_e) { /* ignore */ }
  }
  _tseOrphanUnsub = null;
}

/**
 * Action: einen hängenden Signatur-Vorgang weiterbringen — auf dem Weg, der zu
 * seinem Zustand passt.
 *
 * B1-S259: bis hierher ging JEDER Klick an `tse-reconcile`. Der Job scannt aber
 * ausschliesslich `state = 'pending'` älter als 90 Sekunden und liest die
 * mitgeschickte `tx_id` gar nicht. Die vier Zeilen im Panel sind
 * `orphan_failed` — der Aufruf lieferte `scanned: 0`, änderte nichts, und der
 * Toast las sich trotzdem wie Erfolg. Ein Erfolgs-Toast, hinter dem nichts
 * passiert, ist die schlechteste der drei Möglichkeiten.
 *
 * Jetzt:
 *   · `pending`       → `tse-reconcile` (dafür ist der Job da),
 *   · `orphan_failed` → Nachsignatur über `tse-sign` aus dem gespeicherten
 *     `sign_input` — genau der Weg, den der Kommentar in der Function selbst
 *     nennt, idempotent über die deterministische tx_id.
 *
 * Der Zustand kommt frisch aus der RPC, nicht aus dem DOM: zwischen Render und
 * Klick kann der Reconcile-Job die Zeile längst geschlossen haben.
 */
export async function tseOrphanRetry(txId) {
  if (!txId) return;
  const adapter = window.App && window.App.storage && window.App.storage.tsePendingTx;
  if (!adapter) {
    showToast('TSE-Adapter nicht verfügbar.', 'error');
    return;
  }
  const rowEl = document.querySelector('[data-tse-tx="' + (window.CSS && CSS.escape ? CSS.escape(txId) : txId) + '"]');
  const btn = rowEl ? rowEl.querySelector('button') : null;
  const label = btn ? btn.textContent : '';
  if (btn) { btn.disabled = true; btn.textContent = '… läuft'; }
  try {
    const rows = await adapter.listOrphans({ limit: 200 });
    const row = (rows || []).find(r => r.txId === txId);
    if (!row) {
      showToast('Dieser Vorgang steht nicht mehr offen — die Liste wird neu geladen.', 'success');
      refreshTseOrphanCard();
      return;
    }

    if (row.state === 'orphan_failed') {
      if (typeof window.tseRetryOrphanSignature !== 'function') {
        throw new Error('Nachsignatur-Funktion nicht geladen.');
      }
      const res = await window.tseRetryOrphanSignature(txId, row.signInput);
      showToast('Nachsigniert: TX ' + (res && res.tx_number ? res.tx_number : '—')
              + ' · Zähler ' + (res && res.signature_counter ? res.signature_counter : '—'), 'success');
    } else {
      const result = await adapter.triggerReconcile(txId);
      // `scanned: 0` heisst: der Job hat nichts gefunden, was zu tun war. Das
      // als Erfolg zu melden, war genau der Befund — also benennen.
      const scanned = result && Number(result.scanned || 0);
      if (scanned === 0) {
        showToast('Der Reconcile-Job hat nichts zu tun gefunden — die Zeile ist noch '
                + 'keine 90 Sekunden alt oder inzwischen geschlossen.', 'warning');
      } else {
        const summary = (result && (result.summary || result.outcome))
          || ('geprüft ' + scanned + ' · geschlossen ' + (result.closed || 0)
              + ' · orphan ' + (result.orphan || 0));
        showToast('Reconcile-Ergebnis: ' + summary, 'success');
      }
    }
    refreshTseOrphanCard();
  } catch (err) {
    console.error('[settings] tseOrphanRetry:', err);
    showToast('Fehlgeschlagen: ' + (err.message || err), 'error');
    if (btn) { btn.disabled = false; btn.textContent = label || '🔁 Erneut probieren'; }
  }
}


/**
 * S258 — Aktion: die fehlende Signatur nachholen.
 *
 * Bewusst über denselben Weg wie jede andere Signatur (`tse-sign` →
 * `tse_pending_open` → fiskaly → `record_*_tse_signature`) und NICHT über einen
 * Sonderpfad: die tx_id ist deterministisch, ein zweiter Versuch mit denselben
 * Werten liefert bei fiskaly dieselbe Transaktion. Es entsteht also keine
 * zweite Nummer und kein zweiter Bon — der Beleg bekommt nachträglich genau
 * die Signatur, die er beim Bezahlen hätte bekommen sollen.
 *
 * Die Beträge kommen aus der DB-Zeile, nicht aus der Anzeige: beim Teilbeleg
 * die gespeicherte Aufteilung je Satz (S241/HF-10), bei der Order der
 * Order-Kopf samt Positionen.
 */
export async function tseSignMissing(kind, id) {
  if (!id) return;
  const store = window.App && window.App.storage;
  if (!store) { showToast('Storage-Adapter nicht verfügbar.', 'error'); return; }

  const row = document.querySelector(
    '[data-tse-unsigned="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]');
  const btn = row ? row.querySelector('button') : null;
  if (btn) { btn.disabled = true; btn.textContent = '… signiert'; }

  try {
    if (kind === 'receipt') {
      // Der Beleg wird über seine Eltern-Order geladen — `listReceipts` ist der
      // einzige Leser dafür (kein zweiter Lesepfad, S246-HF).
      const orderId = row ? row.getAttribute('data-tse-order') : null;
      const receipts = await store.orders.listReceipts(orderId || '');
      const receipt = (receipts || []).find(r => r.id === id);
      if (!receipt) throw new Error('Teilbeleg nicht gefunden (' + id + ').');
      if (receipt.tseTransactionId) throw new Error('Dieser Beleg ist bereits signiert.');
      const res = await window.tseSignAndRecordReceipt(receipt);
      if (!res) throw new Error('TSE ist deaktiviert — keine Signatur möglich.');
    } else {
      const order = await store.orders.getFresh(id);
      if (!order) throw new Error('Bestellung nicht gefunden (' + id + ').');
      if (order.tseTransactionId) throw new Error('Diese Bestellung ist bereits signiert.');
      const res = await window.tseSignAndRecord(order);
      if (!res) throw new Error('TSE ist deaktiviert — keine Signatur möglich.');
    }
    showToast('Signatur nachgeholt.', 'success');
    refreshTseOrphanCard();
  } catch (err) {
    console.error('[settings] tseSignMissing:', err);
    showToast('Nachsignieren fehlgeschlagen: ' + (err.message || err), 'error');
    if (btn) { btn.disabled = false; btn.textContent = '🔏 Signatur nachholen'; }
  }
}

/**
 * Bestimmt die Confirm-Dialog-Message beim TSE-Mode-Wechsel. Pure
 * Function — extrahiert für Vitest-Coverage. Liefert `null`, wenn KEIN
 * Confirm nötig ist (z.B. Switch von mock auf disabled — beide nicht
 * KassenSichV-relevant).
 *
 * Regeln (Stand S35):
 * - Wechsel AUF «fiskaly»: Confirm pflichtig (Live-Schaltung).
 * - Wechsel VON «fiskaly» weg: Confirm pflichtig (Außerbetriebnahme §9.6).
 * - prev === next: kein Confirm (sollte vorher abgefangen sein).
 * - mock ↔ disabled: kein Confirm (beide nicht produktiv).
 */
export function buildTseModeSwitchConfirmMessage(prevMode, nextMode) {
  if (prevMode === nextMode) return null;
  if (nextMode === 'fiskaly') {
    return (
      'TSE-Modus auf «fiskaly» (Live) wechseln?\n\n'
      + 'Ab jetzt wird JEDE Bezahlung über die fiskaly Cloud-TSE signiert. '
      + 'Voraussetzung: Provisioning ist abgeschlossen (TSS-ID + Client-ID '
      + 'in der Statusbox sichtbar).\n\n'
      + 'Eine Rücksetzung erfordert ein dokumentiertes Außerbetriebnahme-'
      + 'Protokoll (Verfahrensdoku §9.6).'
    );
  }
  if (prevMode === 'fiskaly') {
    return (
      'WARNUNG: Wechsel vom Live-Modus «fiskaly» auf «' + String(nextMode) + '».\n\n'
      + 'Das bedeutet AUSSERBETRIEBNAHME der scharfen TSE-Signaturen — '
      + 'ab dem Wechsel werden Bezahlungen nicht mehr live signiert. '
      + 'Der Vorgang ist gemäß §9.6 Verfahrensdoku dokumentationspflichtig '
      + 'und muss dem Steuerberater gemeldet werden.\n\n'
      + 'Wirklich umstellen?'
    );
  }
  return null;
}

export async function tseSetMode(mode) {
  // KassenSichV-Best-Practice: vor Wechsel auf/von «fiskaly» Confirm einblenden.
  // Begründung: Wechsel auf «fiskaly» schaltet die produktive Signatur-Pipeline
  // scharf (jede Bezahlung wird ab dann live signiert + im tse_audit_log
  // dokumentiert). Wechsel WEG von «fiskaly» ist eine Außerbetriebnahme im
  // Sinne von §9.6 Verfahrensdoku und löst Doku-Pflichten beim Steuerberater
  // aus. Nicht über one-click kippen lassen.
  let prevMode = 'mock';
  try {
    const curCfg = (typeof window.getTseConfig === 'function')
      ? await window.getTseConfig()
      : null;
    prevMode = (curCfg && curCfg.tse_mode) || 'mock';
  } catch (_) { /* defensiv — bei Lese-Fehler nutzen wir den Default */ }

  if (mode === prevMode) {
    // No-Op — gleiche Mode-Wahl, kein Switch nötig.
    refreshTseSettings();
    return;
  }

  const confirmMsg = buildTseModeSwitchConfirmMessage(prevMode, mode);
  if (confirmMsg && !(await window.styledConfirm({ title: 'TSE-Modus wechseln?', body: confirmMsg, danger: true, confirmLabel: 'Wechseln' })).confirmed) {
    // Abbruch — UI auf alten Zustand zurücksetzen, damit das Radio nicht
    // visuell „hängenbleibt".
    refreshTseSettings();
    return;
  }

  try {
    await window.setTseMode(mode);
    showToast('TSE-Modus: ' + mode, 'success');
    refreshTseSettings();
  } catch (err) {
    console.error('[settings] setTseMode:', err);
    showToast('Modus-Wechsel fehlgeschlagen: ' + (err.message || err), 'error');
    refreshTseSettings();
  }
}

/** S229 T3 — Einmal-Anzeige des Admin-PUK nach Erst-Provisionierung. Der PUK wird
 *  von fiskaly NUR EINMAL geliefert (Verfahrensdoku §9.5) und ist für den LIVE-
 *  Gang zwingend zu notieren. Modal mit Notieren-Pflicht: der Operator muss den
 *  PUK zur Bestätigung exakt abtippen (challenge), sonst bleibt „Notiert" inaktiv;
 *  Abbrechen/ESC re-öffnet das Modal, damit der PUK nicht versehentlich verloren
 *  geht. Kein natives Dialog (Projekt-Prinzip: styled In-App-Dialoge). */
export async function showAdminPukModal(puk) {
  const pk = String(puk || '').trim();
  if (!pk) return;
  if (typeof window === 'undefined' || typeof window.styledConfirm !== 'function') {
    // Fallback (kein Dialog-System verfügbar): PUK wenigstens als Fehler-Toast
    // dauerhaft sichtbar machen, damit er nicht komplett verloren geht.
    showToast('Admin-PUK (EINMALIG notieren!): ' + pk, 'error');
    return;
  }
  const body =
    'fiskaly hat den Admin-PUK erzeugt. Er wird NUR DIESES EINE MAL angezeigt und ' +
    'kann später nicht erneut abgerufen werden. Notiere ihn jetzt sicher (Passwort-' +
    'Manager / Tresor) — er ist für den LIVE-Gang und PIN-Rücksetzungen zwingend.\n\n' +
    'Admin-PUK: ' + pk + '\n\n' +
    'Zur Bestätigung den PUK unten exakt eintippen.';
  // Solange nicht ausdrücklich bestätigt (PUK abgetippt) wird, Modal erneut zeigen.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await window.styledConfirm({
      title: 'Admin-PUK sichern (einmalig!)',
      body,
      danger: true,
      challenge: pk,
      confirmLabel: 'Ich habe den PUK notiert',
      cancelLabel: 'Noch nicht'
    });
    if (res && res.confirmed) break;
  }
}

export async function tseProvision() {
  const root = document.getElementById('tse-settings-card');
  if (root) root.querySelectorAll('button').forEach(b => b.disabled = true);
  try {
    if (!window.App || !window.App.supabase) throw new Error('Supabase-Client nicht verfügbar.');
    const { data, error } = await safeInvoke('tse-provision', {});
    if (error) throw error;
    if (!data || !data.success) throw new Error((data && data.error) || 'Provisioning fehlgeschlagen.');
    showToast(data.was_existing
      ? 'TSS + Client waren bereits provisioniert.'
      : `TSS angelegt (${(data.tss_id || '').slice(0,8)}…)`, 'success');
    refreshTseSettings();
    // S229 T3 — Admin-PUK NUR bei Erst-Provisionierung im Response, und NUR
    // dieses eine Mal (Edge-Kommentar; Verfahrensdoku §9.5). Vorher ging er
    // verloren (nur Toast). Modal mit Notieren-Pflicht: der Operator muss den
    // PUK zur Bestätigung exakt abtippen, bevor der Dialog schließt. Kein
    // natives alert (Projekt-Prinzip: styled In-App-Dialoge).
    if (!data.was_existing && data.admin_puk) {
      await showAdminPukModal(String(data.admin_puk));
    }
  } catch (err) {
    console.error('[settings] tseProvision:', err);
    showToast('Provisioning fehlgeschlagen: ' + (err.message || err), 'error');
    if (root) root.querySelectorAll('button').forEach(b => b.disabled = false);
  }
}

// -----------------------------------------------------------------------------
// Side-Effect: window.* für Inline-Handler im Settings-HTML (onclick).
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    refreshTseSettings,
    refreshTseOrphanCard,
    tseOrphanRetry,
    tseSignMissing,
    tseSetMode,
    tseProvision,
    buildTseModeSwitchConfirmMessage
  });
}
